import { Autocomplete } from '@mui/material';
import { AxiosResponse } from 'axios';
import { memo, useCallback, useMemo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import debounce from 'shell/utils/debounce';
import { noop } from 'shell/utils/noop';
import { Operation } from 'shell/view-models/operation';

export const CODE_APOLIDE = '198';

export const CODE_NAZIONALITA_ITALIA = '90';
export const LABEL_NAZIONALITA_ITALIA = 'ITALIANA';

interface NationalityListComponentProps {
  disabled?: boolean;
  value: NationalityVM | null;
  onChange: (nationality?: NationalityVM | null) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
}

type Key = {
  codice: string;
};

interface Nationality {
  note?: null | string;
  dsNazione?: null | string;
  dsNazioneIso?: string;
  iso?: string;
  codiceCapitale?: number;
  cdAlpha3NazIso?: string;
  nomeCittadino?: string;
  flagUnioneEuropea?: string;
  key?: Key;
  siglaMotorizzazione?: string;
  codIstat?: string;
  codiceOperatoreInserimento?: null | string;
  timestampUltimoAggiornamento?: null | number;
  nomeCittadina?: string;
  idAnpr?: number;
  dsNazioneAnpr?: string;
  endonimo?: null | string;
  nome?: string;
  cdLocaleIso?: string;
  comuni?: null;
  codiceOperatoreUltimoAggiornamento?: null | string;
  belfiore?: string;
  codiceCittadinanza?: number;
  cmnAssegn?: null | string;
  cdAlpha2NazIso?: string;
  timestampInserimento?: null | number;
  flagRilascioCI?: null | string;
  esonimo?: string;
}

export interface NationalityVM {
  idAnpr?: number;
  id: string;
  value: string;
}

const NationalityListComponent = ({
  disabled,
  onChange,
  required,
  value: selectedValue,
  error,
  label = 'Nazionalità',
  id = 'nationality',
}: NationalityListComponentProps): React.ReactElement => {
  const [nationality, getNationality] = useHttpFetch<
    HttpResponseList<Nationality>,
    Array<NationalityVM>
  >(`${__MUNICIPALITY_API_URL__}/geo/stato/idAnpr`, {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<Nationality>>) => {
      const { data } = response;

      return data?.payload.list.map((item) => ({
        id: item.key?.codice || '',
        value: item.nomeCittadina,
        idAnpr: item.idAnpr || '',
      })) as Array<NationalityVM>;
    },
  });

  const defaultProps = useMemo(
    () => ({
      options: (nationality.data as Array<NationalityVM>) || [],
      getOptionLabel: (option: NationalityVM | null) => option?.value || '',
    }),
    [nationality]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const { abort = noop } = nationality;

        abort();

        getNationality({
          params: {},
          paramsSerializer: () => `filters=nomeCittadina:${value}:${Operation.LIKE}`,
        });
      },
      [nationality.abort, getNationality]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 2 && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={nationality.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

NationalityListComponent.displayName = 'Nationality list component';

const NationalityList = memo(NationalityListComponent);

export default NationalityList as typeof NationalityListComponent;
